import { Flex, Form } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import ScheduleDateTimePicker from '@/components/form/shared/ScheduleDateTimePicker';
import { useApiMutation } from '@/hooks/api';
import { queryKeys } from '@/shared/lib/queryKeys';
import { buildProposeDateFormSchema } from '@/utils/validation/formSchemas/jobSchemas';

import type { FormSize } from '@/components/form/SchemaForm/sizing';
import type { SchemaSection } from '@/components/form/SchemaForm/types';
import type { FormInstance } from 'antd/es/form';

export const ADD_PROPOSE_DATE_FORM_ID = 'add-dates';

interface ProposedProps {
  addDates: (dates: Record<string, unknown>[]) => Promise<unknown>;
  interviewComment?: string;
  form?: FormInstance;
  showSubmitButton?: boolean;
  onFooterReady?: (footer: {
    submit: () => void;
    loading: boolean;
    submitLabel: string;
  }) => void;
  size?: FormSize;
}

const AddProposeDate = ({
  addDates,
  interviewComment,
  form: externalForm,
  showSubmitButton = true,
  onFooterReady,
  size,
}: ProposedProps) => {
  const [t] = useTranslation();
  const [internalForm] = Form.useForm();
  const form = externalForm ?? internalForm;
  const formSchema = useMemo(() => buildProposeDateFormSchema(t), [t]);

  const sections: SchemaSection[] = [
    {
      key: 'proposed-date',
      fields: [
        {
          name: 'date_0',
          type: 'custom',
          colSpan: 12,
          label: t('TABLE_COLUMN_DATE'),
          render: ({ inputSize }) => (
            <ScheduleDateTimePicker size={inputSize} />
          ),
        },
        {
          name: 'place_0',
          type: 'text',
          colSpan: 12,
          label: t('PLACE'),
        },
      ],
    },
    {
      key: 'comment',
      title: t('PROPOSE_DATE_COMMENT'),
      description: t('PROPOSE_DATE_COMMENT_SUBHEADER'),
      fields: [
        {
          name: 'comment',
          type: 'textarea',
          componentProps: { autoSize: { minRows: 2, maxRows: 2 } },
        },
      ],
    },
  ];

  const submitMutation = useApiMutation<unknown, Record<string, unknown>>({
    mutationFn: (values) => {
      const proposed_dates = [];
      const dateValue = values.date_0 as {
        format: () => string;
        add: (n: number, unit: string) => { format: () => string };
      };
      const placeValue = values.place_0 as string | undefined;
      if (dateValue && placeValue) {
        proposed_dates.push({
          time_from: dateValue.format(),
          time_to: dateValue.add(1, 'hours').format(),
          place: placeValue,
          comment: values.comment,
        });
      }
      return addDates(proposed_dates);
    },
    invalidateKeys: [queryKeys.interviews.all],
  });

  const onFinish = (values: Record<string, unknown>) => {
    submitMutation.mutate(values);
  };

  useEffect(() => {
    if (showSubmitButton || !onFooterReady) {
      return;
    }

    onFooterReady({
      submit: () => form.submit(),
      loading: submitMutation.loading,
      submitLabel: t('UPDATE_DATE_TEXT'),
    });
  }, [showSubmitButton, onFooterReady, form, submitMutation.loading, t]);

  return (
    <Flex vertical>
      <SchemaForm
        form={form}
        formSchema={formSchema}
        size={size}
        name={ADD_PROPOSE_DATE_FORM_ID}
        scrollToFirstError
        onFinish={onFinish}
        initialValues={{ comment: interviewComment }}
        sections={sections}
        footer={
          showSubmitButton
            ? {
                actions: [
                  {
                    label: t('UPDATE_DATE_TEXT'),
                    htmlType: 'submit',
                    type: 'primary',
                    loading: submitMutation.loading,
                  },
                ],
              }
            : undefined
        }
      />
    </Flex>
  );
};

export default AddProposeDate;
